import { useSieMethods } from '@/hooks/use-sie';

/**
 * TDA: the props that turn the browser's autofill off on the SIGN-IN identifier
 * field.
 *
 * NEW FILE — upstream has no equivalent, so it costs nothing at merge time. It
 * exists rather than an inline literal at the two call sites because the passkey
 * caveat below needs one home, not two copies that can drift apart.
 *
 * ⚠️ Returns props to SPREAD, not a value to assign. `SmartInputField` renders
 * `{...inputHtmlProps}` and then `{...rest}`, so a call-site prop wins —
 * including one explicitly set to `undefined`, which strips the attribute
 * instead of leaving the computed `email`/`tel` in place. An empty object is the
 * only way to say "leave it alone".
 *
 * ⚠️ Yields nothing when passkey sign-in is enabled. WebAuthn conditional UI is
 * armed by `autocomplete="username webauthn"` on this very field
 * (`shared/components/InputFields/SmartInputField/index.tsx`), and overriding it
 * with `off` would turn passkey sign-in off with no error anywhere.
 *
 * ⚠️ `autocomplete="off"` is a request, not a guarantee. Chrome and Safari still
 * offer SAVED CREDENTIALS on anything they read as a login form, and no markup
 * changes that. What it does stop is ordinary form autofill of an email or a
 * phone number.
 *
 * Scoped to the two sign-in forms deliberately: register, forgot password, SSO
 * email, `Continue` and the reset-password screens keep upstream behaviour.
 */
const useIdentifierAutofillProps = (): { autoComplete?: 'off' } => {
  const { passkeySignIn } = useSieMethods();

  return passkeySignIn?.enabled ? {} : { autoComplete: 'off' };
};

export default useIdentifierAutofillProps;
